import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Alert from '../components/Alert';
import LoadingSpinner from '../components/LoadingSpinner';
import ConfirmDialog from '../components/ConfirmDialog';

const OrganizationSettings = () => {
    const { t } = useTranslation();
    const { user, setUser, isAdmin, isSubscriptionActive } = useAuth();
    const location = useLocation();
    const [organization, setOrganization] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState(location.state?.tab || 'general');

    // Confirmation dialog state
    const [confirmDialog, setConfirmDialog] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: null
    });

    // Invite form state
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState('user');
    const [inviteLoading, setInviteLoading] = useState(false);

    // Subscription upgrade state
    const [upgradePlan, setUpgradePlan] = useState('PRO');
    const [upgradeBillingPeriod, setUpgradeBillingPeriod] = useState('monthly');
    const [upgradeLoading, setUpgradeLoading] = useState(false);
    const [cardData, setCardData] = useState({ holderName: '', number: '', expiry: '', cvv: '' });
    const [cardErrors, setCardErrors] = useState({});

    // Form states
    const [organizationName, setOrganizationName] = useState('');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [allowViewAllPatients, setAllowViewAllPatients] = useState(false);
    const [invoiceDetails, setInvoiceDetails] = useState({
        companyName: '',
        vatNumber: '',
        address: '',
        city: '',
        postalCode: '',
        country: '',
        email: '',
        phone: '',
    });

    // Vendor management state
    const [partnerVendors, setPartnerVendors] = useState([]);
    const [availableVendors, setAvailableVendors] = useState([]);
    const [vendorForm, setVendorForm] = useState({
        vendorName: '',
        accountNumber: '',
        orderEmail: '',
        active: true,
        agreement: '',
    });
    const [editingVendorId, setEditingVendorId] = useState(null);
    const [vendorLoading, setVendorLoading] = useState(false);

    useEffect(() => {
        fetchOrganization();
        fetchAvailableVendors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOrganization = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/organizations/my-organization');
            setOrganization(response.data.organization);
            setOrganizationName(response.data.organization.name);
            setLicenseNumber(response.data.organization.licenseNumber || '');
            setAllowViewAllPatients(response.data.organization.settings?.allowViewAllPatients || false);

            // Populate invoice details if they exist
            if (response.data.organization.invoiceDetails) {
                setInvoiceDetails({
                    companyName: response.data.organization.invoiceDetails.companyName || '',
                    vatNumber: response.data.organization.invoiceDetails.vatNumber || '',
                    address: response.data.organization.invoiceDetails.address || '',
                    city: response.data.organization.invoiceDetails.city || '',
                    postalCode: response.data.organization.invoiceDetails.postalCode || '',
                    country: response.data.organization.invoiceDetails.country || '',
                    email: response.data.organization.invoiceDetails.email || '',
                    phone: response.data.organization.invoiceDetails.phone || '',
                });
            }

            // Set partner vendors
            setPartnerVendors(response.data.organization.partnerVendors || []);

            setError('');
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateOrganization = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            await axios.put('/api/organizations/update', {
                name: organizationName,
                licenseNumber: licenseNumber,
                allowViewAllPatients: allowViewAllPatients,
            });
            setSuccess(t('organizationSettings.success.orgUpdated'));
            await fetchOrganization();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.updateFailed'));
        }
    };

    const handleInviteUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const derivedSubscription = inviteRole === 'admin'
            ? (user?.subscription || 'BASIC')
            : inviteRole === 'proUser' ? 'PRO' : 'BASIC';

        try {
            setInviteLoading(true);
            await axios.post('/api/organizations/invite', {
                email: inviteEmail,
                role: inviteRole,
                subscription: derivedSubscription,
            });
            setSuccess(t('organizationSettings.success.invitationSent', { email: inviteEmail }));
            setInviteEmail('');
            setInviteRole('user');
            await fetchOrganization();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.inviteFailed'));
        } finally {
            setInviteLoading(false);
        }
    };

    // Card validation helpers
    const detectCardType = (number) => {
        const d = number.replace(/\D/g, '');
        if (/^4/.test(d)) return 'visa';
        if (/^5[1-5]/.test(d) || /^2[2-7]/.test(d)) return 'mastercard';
        if (/^3[47]/.test(d)) return 'amex';
        return 'unknown';
    };

    const luhnCheck = (number) => {
        const digits = number.replace(/\D/g, '');
        if (!digits.length) return false;
        let sum = 0;
        let isEven = false;
        for (let i = digits.length - 1; i >= 0; i--) {
            let digit = parseInt(digits[i], 10);
            if (isEven) { digit *= 2; if (digit > 9) digit -= 9; }
            sum += digit;
            isEven = !isEven;
        }
        return sum % 10 === 0;
    };

    const handleCardNumberChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 16);
        const formatted = raw.replace(/(.{4})(?=.)/g, '$1 ');
        setCardData(prev => ({ ...prev, number: formatted }));
        if (cardErrors.number) setCardErrors(prev => ({ ...prev, number: '' }));
    };

    const handleExpiryChange = (e) => {
        const raw = e.target.value.replace(/\D/g, '').slice(0, 4);
        const formatted = raw.length >= 3 ? raw.slice(0, 2) + '/' + raw.slice(2) : raw;
        setCardData(prev => ({ ...prev, expiry: formatted }));
        if (cardErrors.expiry) setCardErrors(prev => ({ ...prev, expiry: '' }));
    };

    const validateCard = () => {
        const errors = {};
        const { holderName, number, expiry, cvv } = cardData;
        if (!holderName.trim()) {
            errors.holderName = t('organizationSettings.validation.cardholderRequired');
        }
        const digits = number.replace(/\D/g, '');
        if (digits.length < 13) {
            errors.number = t('organizationSettings.validation.cardTooShort');
        } else if (!luhnCheck(digits)) {
            errors.number = t('organizationSettings.validation.cardInvalid');
        }
        if (expiry.length < 5) {
            errors.expiry = t('organizationSettings.validation.expiryFormat');
        } else {
            const [mm, yy] = expiry.split('/');
            const month = parseInt(mm, 10);
            const year = 2000 + parseInt(yy, 10);
            const now = new Date();
            if (month < 1 || month > 12) {
                errors.expiry = t('organizationSettings.validation.expiryInvalidMonth');
            } else if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
                errors.expiry = t('organizationSettings.validation.expiryExpired');
            }
        }
        const cvvLen = detectCardType(number) === 'amex' ? 4 : 3;
        if (!/^\d+$/.test(cvv) || cvv.length < cvvLen) {
            errors.cvv = t('organizationSettings.validation.cvvInvalid', { count: cvvLen });
        }
        return errors;
    };

    const handleUpgrade = async () => {
        const errors = validateCard();
        if (Object.keys(errors).length > 0) {
            setCardErrors(errors);
            return;
        }
        setCardErrors({});
        setUpgradeLoading(true);
        setError('');
        setSuccess('');
        try {
            // Card validated client-side — integrate a payment processor (e.g. Stripe/Mollie)
            // to tokenize card data before charging in production.
            const response = await axios.put('/api/organizations/upgrade-subscription', {
                plan: upgradePlan,
                billingPeriod: upgradeBillingPeriod,
            });
            setUser(response.data.user);
            setSuccess(t('organizationSettings.success.subscriptionUpgraded'));
            setCardData({ holderName: '', number: '', expiry: '', cvv: '' });
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.upgradeFailed'));
        } finally {
            setUpgradeLoading(false);
        }
    };

    const handleUpdateInvoiceDetails = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            await axios.put('/api/organizations/invoice-details', invoiceDetails);
            setSuccess(t('organizationSettings.success.invoiceUpdated'));
            await fetchOrganization();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.errors.invoiceUpdateFailed'));
        }
    };

    // Vendor management functions
    const fetchAvailableVendors = async () => {
        try {
            const response = await axios.get('/api/product-options/config');
            const vendorTables = response.data.vendorTables || [];

            // Extract unique vendor names
            const vendorMap = new Map();
            vendorTables.forEach(vendor => {
                if (!vendorMap.has(vendor.vendorName)) {
                    vendorMap.set(vendor.vendorName, {
                        vendorName: vendor.vendorName,
                    });
                }
            });

            // Convert Map values to array and sort alphabetically by vendor name
            const uniqueVendors = Array.from(vendorMap.values()).sort((a, b) =>
                a.vendorName.localeCompare(b.vendorName)
            );

            setAvailableVendors(uniqueVendors);
        } catch (err) {
            console.error('Error fetching available vendors:', err);
        }
    };

    const fetchPartnerVendors = async () => {
        try {
            const response = await axios.get('/api/organizations/partner-vendors');
            setPartnerVendors(response.data.partnerVendors || []);
        } catch (err) {
            console.error('Error fetching partner vendors:', err);
        }
    };

    const handleVendorFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setVendorForm({
            ...vendorForm,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleAddOrUpdateVendor = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setVendorLoading(true);

        try {
            if (editingVendorId) {
                // Update existing vendor
                await axios.put(`/api/organizations/partner-vendors/${editingVendorId}`, {
                    accountNumber: vendorForm.accountNumber,
                    orderEmail: vendorForm.orderEmail,
                    active: vendorForm.active,
                    agreement: vendorForm.agreement,
                });
                setSuccess(t('organizationSettings.vendors.success.vendorUpdated'));
            } else {
                // Add new vendor
                await axios.post('/api/organizations/partner-vendors', vendorForm);
                setSuccess(t('organizationSettings.vendors.success.vendorAdded'));
            }

            // Reset form and fetch updated vendors
            setVendorForm({
                vendorName: '',
                accountNumber: '',
                orderEmail: '',
                active: true,
                agreement: '',
            });
            setEditingVendorId(null);
            await fetchOrganization();
            await fetchPartnerVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.vendors.errors.saveFailed'));
        } finally {
            setVendorLoading(false);
        }
    };

    const handleEditVendor = (vendor) => {
        setVendorForm({
            vendorName: vendor.vendorName,
            accountNumber: vendor.accountNumber || '',
            orderEmail: vendor.orderEmail || '',
            active: vendor.active !== undefined ? vendor.active : true,
            agreement: vendor.agreement || '',
        });
        setEditingVendorId(vendor._id);
        setActiveTab('vendors'); // Switch to vendor tab
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDeleteVendor = async (vendor) => {
        const proceed = await new Promise((resolve) => {
            setConfirmDialog({
                isOpen: true,
                title: t('confirmDelete.title'),
                message: t('organizationSettings.vendors.confirmDelete', { name: vendor.vendorName }),
                onConfirm: () => {
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                    resolve(true);
                },
                onCancel: () => {
                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                    resolve(false);
                },
            });
        });

        if (!proceed) return;

        setError('');
        setSuccess('');

        try {
            await axios.delete(`/api/organizations/partner-vendors/${vendor._id}`);
            setSuccess(t('organizationSettings.vendors.success.vendorDeleted'));
            await fetchOrganization();
            await fetchPartnerVendors();
        } catch (err) {
            setError(err.response?.data?.message || t('organizationSettings.vendors.errors.deleteFailed'));
        }
    };

    const handleCancelEdit = () => {
        setVendorForm({
            vendorName: '',
            accountNumber: '',
            active: true,
            agreement: '',
        });
        setEditingVendorId(null);
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-8">
                <LoadingSpinner size="lg" text={t('organizationSettings.loading')} />
            </div>
        );
    }

    if (!organization) {
        return (
            <div className="container mx-auto px-4 py-8">
                <div className="card text-center">
                    {error && <Alert type="error" message={error} onDismiss={() => setError('')} />}
                    <p className="text-gray-600">
                        {error ? t('organizationSettings.loadError') : t('organizationSettings.noOrganization')}
                    </p>
                    {error && (
                        <button onClick={fetchOrganization} className="btn-primary mt-4">
                            {t('organizationSettings.tryAgain')}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="page-transition container mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold mb-2">{t('organizationSettings.title')}</h1>
                <p className="text-gray-600">
                    {t('organizationSettings.subtitle')}
                </p>
            </div>

            {/* Alerts */}
            <Alert type="error" message={error} onDismiss={() => setError('')} />
            <Alert type="success" message={success} onDismiss={() => setSuccess('')} />

            {/* Tabs */}
            <div className="mb-6 border-b">
                <nav className="flex space-x-6">
                    <button
                        onClick={() => setActiveTab('general')}
                        className={`pb-3 px-1 border-b-2 font-medium transition-colors ${activeTab === 'general'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-blue-600'
                            }`}
                    >
                        {t('organizationSettings.tabs.general')}
                    </button>
                    <button
                        onClick={() => setActiveTab('subscription')}
                        className={`pb-3 px-1 border-b-2 font-medium transition-colors ${activeTab === 'subscription'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-blue-600'
                            }`}
                    >
                        {t('organizationSettings.tabs.subscription')}
                    </button>
                    <button
                        onClick={() => setActiveTab('invoicing')}
                        className={`pb-3 px-1 border-b-2 font-medium transition-colors ${activeTab === 'invoicing'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-blue-600'
                            }`}
                    >
                        {t('organizationSettings.tabs.invoicing')}
                    </button>
                    <button
                        onClick={() => setActiveTab('vendors')}
                        className={`pb-3 px-1 border-b-2 font-medium transition-colors ${activeTab === 'vendors'
                            ? 'border-blue-600 text-blue-600'
                            : 'border-transparent text-gray-600 hover:text-blue-600'
                            }`}
                    >
                        {t('organizationSettings.tabs.vendors')}
                    </button>
                    {isAdmin() && (
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${activeTab === 'users'
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-gray-600 hover:text-blue-600'
                                }`}
                        >
                            {t('organizationSettings.tabs.users')}
                        </button>
                    )}
                </nav>
            </div>

            {/* General Settings Tab */}
            {activeTab === 'general' && (
                <div className="card">
                    <h2 className="text-2xl font-semibold mb-6">{t('organizationSettings.general.title')}</h2>

                    <form onSubmit={handleUpdateOrganization} className="space-y-6">
                        <div>
                            <label htmlFor="organizationName" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('organizationSettings.general.orgName')}
                            </label>
                            <input
                                type="text"
                                id="organizationName"
                                required
                                value={organizationName}
                                onChange={(e) => setOrganizationName(e.target.value)}
                                className="input-field"
                                disabled={!isAdmin()}
                            />
                            {!isAdmin() && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('organizationSettings.general.orgNameAdminOnly')}
                                </p>
                            )}
                        </div>

                        <div>
                            <label htmlFor="licenseNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('organizationSettings.general.licenseNumber')}
                            </label>
                            <input
                                type="text"
                                id="licenseNumber"
                                value={licenseNumber}
                                onChange={(e) => setLicenseNumber(e.target.value)}
                                className="input-field"
                                disabled={!isAdmin()}
                                placeholder={t('organizationSettings.general.licenseNumberPlaceholder')}
                            />
                            {!isAdmin() && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('organizationSettings.general.licenseNumberAdminOnly')}
                                </p>
                            )}
                        </div>

                        {/* Allow View All Patients Setting */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <label htmlFor="allowViewAllPatients" className="block text-sm font-medium text-gray-900 mb-1">
                                        {t('organizationSettings.general.allowViewAllPatients.title')}
                                    </label>
                                    <p className="text-xs text-gray-600">
                                        {t('organizationSettings.general.allowViewAllPatients.description')}
                                    </p>
                                </div>
                                <div className="ml-4">
                                    <button
                                        type="button"
                                        onClick={() => isAdmin() && setAllowViewAllPatients(!allowViewAllPatients)}
                                        disabled={!isAdmin()}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${allowViewAllPatients ? 'bg-blue-600' : 'bg-gray-200'
                                            } ${!isAdmin() ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        role="switch"
                                        aria-checked={allowViewAllPatients}
                                        id="allowViewAllPatients"
                                    >
                                        <span
                                            aria-hidden="true"
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${allowViewAllPatients ? 'translate-x-5' : 'translate-x-0'
                                                }`}
                                        />
                                    </button>
                                </div>
                            </div>
                            {allowViewAllPatients && (
                                <div className="mt-3 flex items-start">
                                    <svg className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    <p className="text-xs text-blue-700">
                                        {t('organizationSettings.general.allowViewAllPatients.warning')}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h3 className="font-semibold mb-2">{t('organizationSettings.general.orgInfo')}</h3>
                            <dl className="space-y-2 text-sm">
                                <div className="flex">
                                    <dt className="font-medium text-gray-600 w-32">{t('organizationSettings.general.created')}</dt>
                                    <dd className="text-gray-900">{new Date(organization.createdAt).toLocaleDateString()}</dd>
                                </div>
                                <div className="flex">
                                    <dt className="font-medium text-gray-600 w-32">{t('organizationSettings.general.members')}</dt>
                                    <dd className="text-gray-900">{organization.members.length}</dd>
                                </div>
                                <div className="flex">
                                    <dt className="font-medium text-gray-600 w-32">{t('organizationSettings.general.owner')}</dt>
                                    <dd className="text-gray-900">
                                        {organization.owner.firstName} {organization.owner.lastName}
                                    </dd>
                                </div>
                            </dl>
                        </div>

                        {isAdmin() && (
                            <button type="submit" className="btn-primary">
                                {t('organizationSettings.general.saveChanges')}
                            </button>
                        )}
                    </form>
                </div>
            )}

            {/* Subscription Tab */}
            {activeTab === 'subscription' && (
                <div className="card">
                    <h2 className="text-2xl font-semibold mb-6">{t('organizationSettings.subscription.title')}</h2>

                    {/* Current status */}
                    <div className={`flex items-center justify-between p-4 rounded-lg mb-6 ${!isSubscriptionActive() ? 'bg-red-50 border border-red-200' : 'bg-gray-50'
                        }`}>
                        <div>
                            <p className="text-sm text-gray-600 mb-1">{t('organizationSettings.subscription.currentPlan')}</p>
                            <p className="font-semibold text-gray-900">{user?.subscription}</p>
                            {user?.subscriptionExpiry && (
                                <p className={`text-xs mt-1 ${!isSubscriptionActive() ? 'text-red-600 font-medium' : 'text-gray-500'
                                    }`}>
                                    {!isSubscriptionActive() ? t('organizationSettings.subscription.expired') : t('organizationSettings.subscription.renews')} {new Date(user.subscriptionExpiry).toLocaleDateString()}
                                </p>
                            )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${user?.subscription === 'PRO' && isSubscriptionActive()
                            ? 'bg-green-100 text-green-700'
                            : user?.subscription === 'BASIC' && isSubscriptionActive()
                                ? 'bg-blue-100 text-blue-700'
                                : user?.subscription === 'FREE-TRIAL' && isSubscriptionActive()
                                    ? 'bg-amber-100 text-amber-700'
                                    : 'bg-red-100 text-red-700'
                            }`}>
                            {isSubscriptionActive() ? t('organizationSettings.subscription.active') : t('organizationSettings.subscription.expired')}
                        </span>
                    </div>

                    {/* Plan selector */}
                    <div className="mb-6">
                        <p className="text-sm font-medium text-gray-700 mb-3">{t('organizationSettings.subscription.choosePlan')}</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {['BASIC', 'PRO'].map((plan) => (
                                <button
                                    key={plan}
                                    type="button"
                                    onClick={() => setUpgradePlan(plan)}
                                    className={`p-4 border-2 rounded-lg text-left transition-colors ${upgradePlan === plan
                                        ? 'border-blue-600 bg-blue-50'
                                        : 'border-gray-200 bg-white hover:border-blue-300'
                                        }`}
                                >
                                    <div className="font-semibold text-gray-900 mb-1">
                                        {plan === 'BASIC' ? t('organizationSettings.subscription.basic') : t('organizationSettings.subscription.pro')}
                                    </div>
                                    <div className="text-sm text-gray-600 mb-2">
                                        {upgradeBillingPeriod === 'monthly'
                                            ? (plan === 'BASIC' ? t('organizationSettings.subscription.basicMonthly') : t('organizationSettings.subscription.proMonthly'))
                                            : (plan === 'BASIC' ? t('organizationSettings.subscription.basicYearly') : t('organizationSettings.subscription.proYearly'))}
                                    </div>
                                    <ul className="text-xs text-gray-500 space-y-1">
                                        <li>✓ {t('organizationSettings.subscription.featureOffTheShelf')}</li>
                                        {plan === 'PRO' && <li>✓ {t('organizationSettings.subscription.featureMadeToMeasure')}</li>}
                                    </ul>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Billing period toggle */}
                    <div className="flex items-center gap-4 mb-6">
                        <span className={`text-sm ${upgradeBillingPeriod === 'monthly' ? 'font-semibold text-gray-900' : 'text-gray-500'
                            }`}>{t('organizationSettings.subscription.monthly')}</span>
                        <button
                            type="button"
                            onClick={() => setUpgradeBillingPeriod(prev => prev === 'monthly' ? 'yearly' : 'monthly')}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${upgradeBillingPeriod === 'yearly' ? 'bg-blue-600' : 'bg-gray-200'
                                }`}
                        >
                            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${upgradeBillingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                                }`} />
                        </button>
                        <span className={`text-sm ${upgradeBillingPeriod === 'yearly' ? 'font-semibold text-gray-900' : 'text-gray-500'
                            }`}>
                            {t('organizationSettings.subscription.yearly')} <span className="text-green-600 font-medium">{t('organizationSettings.subscription.yearlySavings')}</span>
                        </span>
                    </div>

                    {/* Payment Details */}
                    <div className="border-t pt-6 mt-2">
                        <div className="flex items-center gap-2 mb-5">
                            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            </svg>
                            <p className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t('organizationSettings.subscription.paymentDetails')}</p>
                            <div className="ml-auto flex items-center gap-1">
                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">VISA</span>
                                <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded font-semibold">MC</span>
                                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded font-semibold">AMEX</span>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {/* Cardholder name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.subscription.cardholderName')}
                                </label>
                                <input
                                    type="text"
                                    value={cardData.holderName}
                                    onChange={(e) => {
                                        setCardData(prev => ({ ...prev, holderName: e.target.value }));
                                        if (cardErrors.holderName) setCardErrors(prev => ({ ...prev, holderName: '' }));
                                    }}
                                    placeholder={t('organizationSettings.subscription.cardholderNamePlaceholder')}
                                    autoComplete="cc-name"
                                    className={`input-field ${cardErrors.holderName ? 'border-red-400 focus:ring-red-400' : ''}`}
                                />
                                {cardErrors.holderName && <p className="text-xs text-red-600 mt-1">{cardErrors.holderName}</p>}
                            </div>

                            {/* Card number */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.subscription.cardNumber')}
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={cardData.number}
                                        onChange={handleCardNumberChange}
                                        placeholder="1234 5678 9012 3456"
                                        autoComplete="cc-number"
                                        inputMode="numeric"
                                        maxLength={19}
                                        className={`input-field pr-16 ${cardErrors.number ? 'border-red-400 focus:ring-red-400' : ''}`}
                                    />
                                    {cardData.number.replace(/\D/g, '').length >= 1 && (
                                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold px-1.5 py-0.5 rounded ${detectCardType(cardData.number) === 'visa' ? 'bg-blue-100 text-blue-700' :
                                            detectCardType(cardData.number) === 'mastercard' ? 'bg-red-100 text-red-600' :
                                                detectCardType(cardData.number) === 'amex' ? 'bg-gray-100 text-gray-700' : ''
                                            }`}>
                                            {detectCardType(cardData.number) === 'visa' ? 'VISA' :
                                                detectCardType(cardData.number) === 'mastercard' ? 'MC' :
                                                    detectCardType(cardData.number) === 'amex' ? 'AMEX' : ''}
                                        </span>
                                    )}
                                </div>
                                {cardErrors.number && <p className="text-xs text-red-600 mt-1">{cardErrors.number}</p>}
                            </div>

                            {/* Expiry + CVV */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.subscription.expiryDate')}
                                    </label>
                                    <input
                                        type="text"
                                        value={cardData.expiry}
                                        onChange={handleExpiryChange}
                                        placeholder={t('organizationSettings.validation.expiryPlaceholder')}
                                        autoComplete="cc-exp"
                                        inputMode="numeric"
                                        maxLength={5}
                                        className={`input-field ${cardErrors.expiry ? 'border-red-400 focus:ring-red-400' : ''}`}
                                    />
                                    {cardErrors.expiry && <p className="text-xs text-red-600 mt-1">{cardErrors.expiry}</p>}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.subscription.cvv')}
                                    </label>
                                    <input
                                        type="password"
                                        value={cardData.cvv}
                                        onChange={(e) => {
                                            const val = e.target.value.replace(/\D/g, '').slice(0, detectCardType(cardData.number) === 'amex' ? 4 : 3);
                                            setCardData(prev => ({ ...prev, cvv: val }));
                                            if (cardErrors.cvv) setCardErrors(prev => ({ ...prev, cvv: '' }));
                                        }}
                                        placeholder={detectCardType(cardData.number) === 'amex' ? t('organizationSettings.subscription.cvvDigitsAmex') : t('organizationSettings.subscription.cvvDigitsOther')}
                                        autoComplete="cc-csc"
                                        inputMode="numeric"
                                        className={`input-field ${cardErrors.cvv ? 'border-red-400 focus:ring-red-400' : ''}`}
                                    />
                                    {cardErrors.cvv && <p className="text-xs text-red-600 mt-1">{cardErrors.cvv}</p>}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap items-center gap-4">
                            <button
                                type="button"
                                onClick={handleUpgrade}
                                disabled={upgradeLoading}
                                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {upgradeLoading ? <LoadingSpinner size="sm" /> : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                        {!isSubscriptionActive() ? t('organizationSettings.subscription.payNow') : t('organizationSettings.subscription.confirmUpgrade')}
                                    </>
                                )}
                            </button>
                            <p className="text-xs text-gray-500 flex items-center gap-1">
                                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                {t('organizationSettings.subscription.sslSecurity')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Invoicing Details Tab */}
            {activeTab === 'invoicing' && (
                <div className="card">
                    <h2 className="text-2xl font-semibold mb-6">{t('organizationSettings.invoicing.title')}</h2>

                    <form onSubmit={handleUpdateInvoiceDetails} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.invoicing.companyName')}
                                </label>
                                <input
                                    type="text"
                                    id="companyName"
                                    value={invoiceDetails.companyName}
                                    onChange={(e) => setInvoiceDetails({ ...invoiceDetails, companyName: e.target.value })}
                                    className="input-field"
                                    disabled={!isAdmin()}
                                />
                            </div>

                            <div>
                                <label htmlFor="vatNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.invoicing.vatNumber')}
                                </label>
                                <input
                                    type="text"
                                    id="vatNumber"
                                    value={invoiceDetails.vatNumber}
                                    onChange={(e) => setInvoiceDetails({ ...invoiceDetails, vatNumber: e.target.value })}
                                    className="input-field"
                                    disabled={!isAdmin()}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                                {t('organizationSettings.invoicing.address')}
                            </label>
                            <input
                                type="text"
                                id="address"
                                value={invoiceDetails.address}
                                onChange={(e) => setInvoiceDetails({ ...invoiceDetails, address: e.target.value })}
                                className="input-field"
                                disabled={!isAdmin()}
                            />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.invoicing.city')}
                                </label>
                                <input
                                    type="text"
                                    id="city"
                                    value={invoiceDetails.city}
                                    onChange={(e) => setInvoiceDetails({ ...invoiceDetails, city: e.target.value })}
                                    className="input-field"
                                    disabled={!isAdmin()}
                                />
                            </div>

                            <div>
                                <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.invoicing.postalCode')}
                                </label>
                                <input
                                    type="text"
                                    id="postalCode"
                                    value={invoiceDetails.postalCode}
                                    onChange={(e) => setInvoiceDetails({ ...invoiceDetails, postalCode: e.target.value })}
                                    className="input-field"
                                    disabled={!isAdmin()}
                                />
                            </div>

                            <div>
                                <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.invoicing.country')}
                                </label>
                                <input
                                    type="text"
                                    id="country"
                                    value={invoiceDetails.country}
                                    onChange={(e) => setInvoiceDetails({ ...invoiceDetails, country: e.target.value })}
                                    className="input-field"
                                    disabled={!isAdmin()}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="invoiceEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.invoicing.email')}
                                </label>
                                <input
                                    type="email"
                                    id="invoiceEmail"
                                    value={invoiceDetails.email}
                                    onChange={(e) => setInvoiceDetails({ ...invoiceDetails, email: e.target.value })}
                                    className="input-field"
                                    disabled={!isAdmin()}
                                />
                            </div>

                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('organizationSettings.invoicing.phone')}
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    value={invoiceDetails.phone}
                                    onChange={(e) => setInvoiceDetails({ ...invoiceDetails, phone: e.target.value })}
                                    className="input-field"
                                    disabled={!isAdmin()}
                                />
                            </div>
                        </div>

                        {!isAdmin() && (
                            <p className="text-sm text-gray-500">
                                {t('organizationSettings.invoicing.adminOnly')}
                            </p>
                        )}

                        {isAdmin() && (
                            <button type="submit" className="btn-primary">
                                {t('organizationSettings.invoicing.saveButton')}
                            </button>
                        )}
                    </form>
                </div>
            )}

            {/* User Management Tab */}
            {activeTab === 'users' && isAdmin() && (
                <div>
                    {/* Invite User Form - Admin Only */}
                    <div className="card mb-6">
                        <h2 className="text-xl font-semibold mb-4">{t('organizationSettings.users.inviteTitle')}</h2>
                        <form onSubmit={handleInviteUser} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="inviteEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.users.emailLabel')}
                                    </label>
                                    <input
                                        type="email"
                                        id="inviteEmail"
                                        required
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        className="input-field"
                                        placeholder={t('organizationSettings.users.emailPlaceholder')}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="inviteRole" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.users.roleLabel')}
                                    </label>
                                    <select
                                        id="inviteRole"
                                        value={inviteRole}
                                        onChange={(e) => setInviteRole(e.target.value)}
                                        className="input-field"
                                    >
                                        <option value="user">{t('organizationSettings.users.roleUser')}</option>
                                        <option value="proUser">{t('organizationSettings.users.roleProUser')}</option>
                                        <option value="admin">{t('organizationSettings.users.roleAdmin')}</option>
                                    </select>
                                </div>
                            </div>

                            {/* Auto-derived subscription badge */}
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">{t('organizationSettings.users.subscriptionPlan')}</span>
                                {(() => {
                                    const sub = inviteRole === 'admin'
                                        ? (user?.subscription || 'BASIC')
                                        : inviteRole === 'proUser' ? 'PRO' : 'BASIC';
                                    return (
                                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${sub === 'PRO' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                            {sub}
                                        </span>
                                    );
                                })()}
                                {inviteRole === 'admin' && (
                                    <span className="text-xs text-gray-500">{t('organizationSettings.users.mustMatchPlan')}</span>
                                )}
                            </div>

                            <button type="submit" disabled={inviteLoading} className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed">
                                {inviteLoading ? t('organizationSettings.users.sending') : t('organizationSettings.users.sendInvitation')}
                            </button>
                        </form>
                    </div>

                    {/* Organization Members */}
                    <div className="card">
                        <h2 className="text-xl font-semibold mb-4">{t('organizationSettings.users.membersTitle')}</h2>

                        <div className="space-y-3">
                            {organization.members.map((member) => (
                                <div
                                    key={member._id}
                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                                >
                                    <div className="flex items-center space-x-4">
                                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                            <span className="text-blue-600 font-semibold">
                                                {member.user.firstName[0]}{member.user.lastName[0]}
                                            </span>
                                        </div>
                                        <div>
                                            <p className="font-semibold">
                                                {member.user.firstName} {member.user.lastName}
                                                {organization.owner._id === member.user._id && (
                                                    <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                                                        {t('organizationSettings.users.owner')}
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-sm text-gray-600">{member.user.email}</p>
                                            <p className="text-xs text-gray-500">
                                                {t('organizationSettings.users.joined', { date: new Date(member.joinedAt).toLocaleDateString() })}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-3">
                                        {isAdmin() && organization.owner._id !== member.user._id ? (
                                            <>
                                                <select
                                                    value={member.role}
                                                    onChange={async (e) => {
                                                        setError('');
                                                        setSuccess('');
                                                        try {
                                                            await axios.put(`/api/organizations/members/${member.user._id}/role`, {
                                                                role: e.target.value,
                                                            });
                                                            setSuccess(t('organizationSettings.success.roleUpdated'));
                                                            await fetchOrganization();
                                                        } catch (err) {
                                                            setError(err.response?.data?.message || t('organizationSettings.errors.roleUpdateFailed'));
                                                        }
                                                    }}
                                                    className="input-field text-sm"
                                                >
                                                    <option value="user">{t('organizationSettings.users.roleUserShort')}</option>
                                                    <option value="proUser">{t('organizationSettings.users.roleProUserShort')}</option>
                                                    <option value="admin">{t('organizationSettings.users.roleAdminShort')}</option>
                                                </select>
                                                <button
                                                    onClick={async () => {
                                                        const memberName = `${member.user.firstName} ${member.user.lastName}`;
                                                        const proceed = await new Promise((resolve) => {
                                                            setConfirmDialog({
                                                                isOpen: true,
                                                                title: t('confirmDelete.title'),
                                                                message: t('confirmDelete.member', { name: memberName }),
                                                                onConfirm: () => {
                                                                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                                                                    resolve(true);
                                                                },
                                                                onCancel: () => {
                                                                    setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null });
                                                                    resolve(false);
                                                                }
                                                            });
                                                        });

                                                        if (!proceed) return;

                                                        setError('');
                                                        setSuccess('');
                                                        try {
                                                            await axios.delete(`/api/organizations/members/${member.user._id}`);
                                                            setSuccess(t('organizationSettings.success.memberRemoved'));
                                                            await fetchOrganization();
                                                        } catch (err) {
                                                            setError(err.response?.data?.message || t('organizationSettings.errors.removeMemberFailed'));
                                                        }
                                                    }}
                                                    className="text-red-600 hover:text-red-700 p-2"
                                                    title={t('organizationSettings.users.removeMember')}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </>
                                        ) : (
                                            <span className="text-sm bg-gray-200 px-3 py-1 rounded capitalize">
                                                {member.role}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Pending Invitations */}
                    {organization.invitations && organization.invitations.length > 0 && (
                        <div className="card mt-6">
                            <h2 className="text-xl font-semibold mb-4">{t('organizationSettings.users.pendingTitle')}</h2>

                            <div className="space-y-3">
                                {organization.invitations
                                    .filter(inv => new Date(inv.expiresAt) > new Date())
                                    .map((invitation) => (
                                        <div
                                            key={invitation._id}
                                            className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                                        >
                                            <div>
                                                <p className="font-semibold">{invitation.email}</p>
                                                <p className="text-sm text-gray-600">
                                                    {t('organizationSettings.users.role')} <span className="capitalize">{invitation.role}</span>
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {t('organizationSettings.users.expires', { date: new Date(invitation.expiresAt).toLocaleDateString() })}
                                                </p>
                                                {invitation.invitedBy && (
                                                    <p className="text-xs text-gray-500">
                                                        {t('organizationSettings.users.invitedBy', { name: `${invitation.invitedBy.firstName} ${invitation.invitedBy.lastName}` })}
                                                    </p>
                                                )}
                                            </div>

                                            <button
                                                onClick={async () => {
                                                    setError('');
                                                    setSuccess('');
                                                    try {
                                                        await axios.delete(`/api/organizations/invitations/${invitation._id}`);
                                                        setSuccess(t('organizationSettings.success.invitationCancelled'));
                                                        await fetchOrganization();
                                                    } catch (err) {
                                                        setError(err.response?.data?.message || t('organizationSettings.errors.cancelInvitationFailed'));
                                                    }
                                                }}
                                                className="text-red-600 hover:text-red-700 text-sm font-semibold"
                                            >
                                                {t('organizationSettings.users.cancelInvitation')}
                                            </button>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Vendors Tab */}
            {activeTab === 'vendors' && (
                <div className="space-y-6">
                    {/* Add/Edit Vendor Form */}
                    {isAdmin() && (
                        <div className="card">
                            <h2 className="text-2xl font-semibold mb-6">
                                {editingVendorId
                                    ? t('organizationSettings.vendors.editTitle')
                                    : t('organizationSettings.vendors.addTitle')}
                            </h2>

                            <form onSubmit={handleAddOrUpdateVendor} className="space-y-6">
                                <div>
                                    <label htmlFor="vendorName" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.vendors.vendorName')} *
                                    </label>
                                    <select
                                        id="vendorName"
                                        name="vendorName"
                                        required
                                        value={vendorForm.vendorName}
                                        onChange={handleVendorFormChange}
                                        disabled={editingVendorId !== null}
                                        className="input-field disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    >
                                        <option value="">{t('organizationSettings.vendors.selectVendor')}</option>
                                        {availableVendors.map((vendor) => (
                                            <option key={vendor.vendorName} value={vendor.vendorName}>
                                                {vendor.vendorName}
                                            </option>
                                        ))}
                                    </select>
                                    {editingVendorId && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('organizationSettings.vendors.cannotChangeVendor')}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.vendors.accountNumber')}
                                    </label>
                                    <input
                                        type="text"
                                        id="accountNumber"
                                        name="accountNumber"
                                        value={vendorForm.accountNumber}
                                        onChange={handleVendorFormChange}
                                        className="input-field"
                                        placeholder={t('organizationSettings.vendors.accountNumberPlaceholder')}
                                    />
                                </div>

                                <div>
                                    <label htmlFor="orderEmail" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.vendors.orderEmail')}
                                    </label>
                                    <input
                                        type="email"
                                        id="orderEmail"
                                        name="orderEmail"
                                        value={vendorForm.orderEmail}
                                        onChange={handleVendorFormChange}
                                        className="input-field"
                                        placeholder={t('organizationSettings.vendors.orderEmailPlaceholder')}
                                    />
                                </div>

                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="active"
                                        name="active"
                                        checked={vendorForm.active}
                                        onChange={handleVendorFormChange}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="active" className="ml-2 block text-sm text-gray-700">
                                        {t('organizationSettings.vendors.active')}
                                    </label>
                                </div>

                                <div>
                                    <label htmlFor="agreement" className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('organizationSettings.vendors.agreement')}
                                    </label>
                                    <textarea
                                        id="agreement"
                                        name="agreement"
                                        rows="4"
                                        value={vendorForm.agreement}
                                        onChange={handleVendorFormChange}
                                        className="input-field"
                                        placeholder={t('organizationSettings.vendors.agreementPlaceholder')}
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="submit"
                                        disabled={vendorLoading}
                                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {vendorLoading
                                            ? t('common.loading')
                                            : editingVendorId
                                                ? t('organizationSettings.vendors.updateButton')
                                                : t('organizationSettings.vendors.addButton')}
                                    </button>
                                    {editingVendorId && (
                                        <button
                                            type="button"
                                            onClick={handleCancelEdit}
                                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    )}

                    {/* Partner Vendors List */}
                    <div className="card">
                        <h2 className="text-2xl font-semibold mb-6">{t('organizationSettings.vendors.listTitle')}</h2>

                        {partnerVendors.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                {t('organizationSettings.vendors.noVendors')}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {partnerVendors.map((vendor) => (
                                    <div
                                        key={vendor._id}
                                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3">
                                                <h3 className="font-semibold text-lg">{vendor.vendorName}</h3>
                                                {vendor.active ? (
                                                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                                                        {t('organizationSettings.vendors.statusActive')}
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-1 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                                                        {t('organizationSettings.vendors.statusInactive')}
                                                    </span>
                                                )}
                                            </div>
                                            {vendor.accountNumber && (
                                                <p className="text-sm text-gray-600 mt-1">
                                                    <span className="font-medium">{t('organizationSettings.vendors.accountNumber')}:</span> {vendor.accountNumber}
                                                </p>
                                            )}
                                            {vendor.orderEmail && (
                                                <p className="text-sm text-gray-600 mt-1">
                                                    <span className="font-medium">{t('organizationSettings.vendors.orderEmail')}:</span> {vendor.orderEmail}
                                                </p>
                                            )}
                                            {vendor.agreement && (
                                                <p className="text-sm text-gray-600 mt-2">
                                                    <span className="font-medium">{t('organizationSettings.vendors.agreement')}:</span> {vendor.agreement}
                                                </p>
                                            )}
                                            {vendor.addedAt && (
                                                <p className="text-xs text-gray-500 mt-2">
                                                    {t('organizationSettings.vendors.addedOn', {
                                                        date: new Date(vendor.addedAt).toLocaleDateString()
                                                    })}
                                                </p>
                                            )}
                                        </div>

                                        {isAdmin() && (
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleEditVendor(vendor)}
                                                    className="text-blue-600 hover:text-blue-700 p-2"
                                                    title={t('common.edit')}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteVendor(vendor)}
                                                    className="text-red-600 hover:text-red-700 p-2"
                                                    title={t('common.delete')}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Confirmation Dialog */}
            <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onCancel={() => setConfirmDialog({ isOpen: false, title: '', message: '', onConfirm: null })}
            />
        </div>
    );
};

export default OrganizationSettings;
